import { useState } from 'react';
import { ReactNode } from 'react';
import LazyLoad from 'react-lazyload';
import { defineMessages } from '@alltrails/shared/react-intl';
import Button from '@alltrails/shared/denali/components/Button';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import ArrowRec from '@alltrails/shared/icons/ArrowRec';
import logHomepageSignupButtonTapped from '@alltrails/analytics/events/logHomepageSignupButtonTapped';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import * as styles from './styles/styles.module.scss';
import { LanguageSupportUtil } from '../../../utils/language_support_util';

const DESKTOP_FEATURE_STRINGS = defineMessages({
  SEARCH_DISCOVER_NAV: {
    defaultMessage: 'Search & discover'
  },
  NAVIGATE_TRACK_NAV: {
    defaultMessage: 'Navigate & track'
  },
  REMEMBER_INSPIRE_NAV: {
    defaultMessage: 'Remember & inspire'
  },
  SEARCH_DISCOVER_TITLE: {
    defaultMessage: 'Pick the right trail for your day'
  },
  NAVIGATE_TRACK_TITLE: {
    defaultMessage: 'Navigate & track'
  },
  REMEMBER_INSPIRE_TITLE: {
    defaultMessage: 'Keep track of your travels'
  },
  SEARCH_DISCOVER_TEXT: {
    defaultMessage: 'All our trails are verified by experts and reviewed by our global community of adventurers like you.'
  },
  NAVIGATE_TRACK_TEXT: {
    defaultMessage: 'Out on the trail, you can keep an eye on your planned route, along with elevation changes and waypoints.'
  },
  REMEMBER_INSPIRE_TEXT: {
    defaultMessage: 'Log all your trails in one place, and share insights to inspire and inform fellow adventurers.'
  },
  SIGNUP: {
    defaultMessage: 'Sign up'
  },
  NAV_NEXT: {
    defaultMessage: 'Navigate next'
  },
  NAV_PREV: {
    defaultMessage: 'Navigate previous'
  }
});

const CDN_BASE = 'https://cdn-assets.alltrails.com/assets/images/homepage/explore_and_discover/';

type Props = {
  languageRegionCode: LanguageRegionCode;
};

const DesktopFeatures = ({ languageRegionCode }: Props): JSX.Element => {
  const {
    formattedDefaultMessages: {
      SEARCH_DISCOVER_NAV,
      NAVIGATE_TRACK_NAV,
      REMEMBER_INSPIRE_NAV,
      SEARCH_DISCOVER_TITLE,
      NAVIGATE_TRACK_TITLE,
      REMEMBER_INSPIRE_TITLE,
      SEARCH_DISCOVER_TEXT,
      NAVIGATE_TRACK_TEXT,
      REMEMBER_INSPIRE_TEXT,
      SIGNUP,
      NAV_NEXT,
      NAV_PREV
    }
  } = useFormatMessage(DESKTOP_FEATURE_STRINGS);
  const [currentNav, setCurrentNav] = useState(0);
  const [nextNav, setNextNav] = useState(0);
  const [opacity, setOpacity] = useState(1);

  const SLIDE_TITLES = [SEARCH_DISCOVER_TITLE, NAVIGATE_TRACK_TITLE, REMEMBER_INSPIRE_TITLE];
  const SLIDE_TEXTS = [SEARCH_DISCOVER_TEXT, NAVIGATE_TRACK_TEXT, REMEMBER_INSPIRE_TEXT];

  const partialImagePath = ['search_discover', 'navigate', 'remember'][currentNav];
  const imageUrl = `${CDN_BASE}homepage-screens-${partialImagePath}_${languageRegionCode}.png`;
  const title = SLIDE_TITLES[currentNav];
  const text = SLIDE_TEXTS[currentNav];

  const handleNavClick = (event: React.MouseEvent, index: number) => {
    event.preventDefault();
    setNextNav(index);
    setOpacity(0);
  };

  const handlePrevNextClick = (event: React.MouseEvent, previousOrNext: string) => {
    event.preventDefault();
    let previousOrNextNav = currentNav + (previousOrNext === 'previous' ? -1 : 1);

    if (previousOrNextNav < 0) {
      previousOrNextNav = 2;
    } else if (nextNav >= 2 && previousOrNext === 'next') {
      previousOrNextNav = 0;
    }

    setNextNav(previousOrNextNav);
    setOpacity(0);
  };

  const handleTransitionEnd = () => {
    if (opacity === 0) {
      setOpacity(1);
      setCurrentNav(nextNav);
    }
  };

  return (
    <div className={styles.bottomContainer}>
      <div className={styles.nav}>
        <div className={styles.upArrow}>
          <IconButton
            icon={{ Component: ArrowRec }}
            linkInfo={{ href: '#previous' }}
            onClick={e => handlePrevNextClick(e, 'previous')}
            testId="desktop-features-previous"
            title={NAV_PREV}
            size="lg"
            variant="transparent"
          />
        </div>
        <a
          className={`${styles.navLink} ${currentNav === 0 ? styles.active : ''}`}
          href="#search-and-discover"
          onClick={event => {
            handleNavClick(event, 0);
          }}
        >
          {SEARCH_DISCOVER_NAV}
        </a>
        <a
          className={`${styles.navLink} ${currentNav === 1 ? styles.active : ''}`}
          href="#navigate-and-track"
          onClick={event => {
            handleNavClick(event, 1);
          }}
        >
          {NAVIGATE_TRACK_NAV}
        </a>
        <a
          className={`${styles.navLink} ${styles.last} ${currentNav === 2 ? styles.active : ''}`}
          href="#remember-and-inspire"
          onClick={event => {
            handleNavClick(event, 2);
          }}
        >
          {REMEMBER_INSPIRE_NAV}
        </a>
        <div className={styles.downArrow}>
          <IconButton
            icon={{ Component: ArrowRec, orientation: 'down' }}
            linkInfo={{ href: '#next' }}
            onClick={e => handlePrevNextClick(e, 'next')}
            testId="desktop-features-next"
            title={NAV_NEXT}
            size="lg"
            variant="transparent"
          />
        </div>
      </div>
      <div className={styles.screen}>
        <LazyLoad>
          <img style={{ opacity }} className={styles.screenImage} src={imageUrl} alt="" onTransitionEnd={handleTransitionEnd} />
        </LazyLoad>
      </div>
      <div style={{ opacity }} className={styles.feature}>
        <div className={styles.featureContent}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.featureText}>{text}</p>
          <div className={styles.signUpLink}>
            <Button
              text={SIGNUP}
              linkInfo={{ href: LanguageSupportUtil.wrapUrlSafe('/signup?ref=homepage_signup_desktopfeatures', languageRegionCode) }}
              onClick={logHomepageSignupButtonTapped}
              testId="explore-and-discover-sign-up"
              variant="default"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopFeatures;
